/**
 * Minimal recursive-descent JSON parser that also records the 0-indexed source line each value
 * starts on. `JSON.parse` throws away position info, so a validation issue that only knows a
 * JSON path (e.g. `data[0].fields[0].source`) can't otherwise be pointed at a line in the editor.
 * Input is assumed to already be valid JSON — callers only run this after `JSON.parse` succeeded.
 */

export type JsonPathSegment = string | number;

export type JsonAstNode =
    | { kind: "object"; properties: Array<{ key: string; value: JsonAstNode }>; line: number }
    | { kind: "array"; items: JsonAstNode[]; line: number }
    | { kind: "scalar"; value: string | number | boolean | null; line: number };

const NUMBER_PATTERN = /^-?\d+(\.\d+)?([eE][+-]?\d+)?/;

class JsonPositionParser {
    private i = 0;
    private line = 0;

    constructor(private readonly text: string) {}

    parse(): JsonAstNode {
        return this.parseValue();
    }

    private advance(count = 1): void {
        for (let n = 0; n < count; n++) {
            if (this.text[this.i] === "\n") this.line++;
            this.i++;
        }
    }

    private skipWhitespace(): void {
        while (this.i < this.text.length && /\s/.test(this.text[this.i])) {
            this.advance();
        }
    }

    private parseValue(): JsonAstNode {
        this.skipWhitespace();
        const char = this.text[this.i];
        if (char === "{") return this.parseObject();
        if (char === "[") return this.parseArray();
        if (char === '"') {
            const { value, line } = this.parseStringLiteral();
            return { kind: "scalar", value, line };
        }
        return this.parseLiteralScalar();
    }

    private parseStringLiteral(): { value: string; line: number } {
        const line = this.line;
        const start = this.i;
        this.advance(); // opening quote
        // Bounded by text length: an unterminated string (e.g. mid-typing in the editor, where
        // content isn't necessarily valid JSON yet) must not spin forever looking for a closing
        // quote that was never written.
        while (this.i < this.text.length && this.text[this.i] !== '"') {
            if (this.text[this.i] === "\\" && this.i + 1 < this.text.length) {
                this.advance(2);
            } else {
                this.advance();
            }
        }
        if (this.text[this.i] === '"') {
            this.advance(); // closing quote
        }
        const raw = this.text.slice(start, this.i);
        let value: string;
        try {
            value = JSON.parse(raw) as string;
        } catch {
            value = raw;
        }
        return { value, line };
    }

    private parseLiteralScalar(): JsonAstNode {
        const line = this.line;
        const rest = this.text.slice(this.i);

        if (rest.startsWith("true")) {
            this.advance(4);
            return { kind: "scalar", value: true, line };
        }
        if (rest.startsWith("false")) {
            this.advance(5);
            return { kind: "scalar", value: false, line };
        }
        if (rest.startsWith("null")) {
            this.advance(4);
            return { kind: "scalar", value: null, line };
        }

        const match = rest.match(NUMBER_PATTERN);
        const token = match?.[0] ?? "";
        this.advance(token.length || 1);
        return { kind: "scalar", value: token ? Number(token) : null, line };
    }

    private parseObject(): JsonAstNode {
        const line = this.line;
        this.advance(); // {
        const properties: Array<{ key: string; value: JsonAstNode }> = [];
        this.skipWhitespace();
        while (this.i < this.text.length && this.text[this.i] !== "}") {
            const { value: key } = this.parseStringLiteral();
            this.skipWhitespace();
            this.advance(); // :
            const value = this.parseValue();
            properties.push({ key, value });
            this.skipWhitespace();
            if (this.text[this.i] === ",") {
                this.advance();
                this.skipWhitespace();
            }
        }
        this.advance(); // }
        return { kind: "object", properties, line };
    }

    private parseArray(): JsonAstNode {
        const line = this.line;
        this.advance(); // [
        const items: JsonAstNode[] = [];
        this.skipWhitespace();
        while (this.i < this.text.length && this.text[this.i] !== "]") {
            items.push(this.parseValue());
            this.skipWhitespace();
            if (this.text[this.i] === ",") {
                this.advance();
                this.skipWhitespace();
            }
        }
        this.advance(); // ]
        return { kind: "array", items, line };
    }
}

export function parseJsonWithPositions(text: string): JsonAstNode {
    return new JsonPositionParser(text).parse();
}

export function resolveJsonPath(node: JsonAstNode | undefined, path: JsonPathSegment[]): JsonAstNode | undefined {
    let current = node;
    for (const segment of path) {
        if (!current) return undefined;
        if (typeof segment === "number") {
            if (current.kind !== "array") return undefined;
            current = current.items[segment];
        } else {
            if (current.kind !== "object") return undefined;
            current = current.properties.find((p) => p.key === segment)?.value;
        }
    }
    return current;
}

/** Finds the first item in a JSON array node whose object property `key` equals `value`. */
export function findArrayItemByKey(
    arrayNode: JsonAstNode | undefined,
    key: string,
    value: string
): JsonAstNode | undefined {
    if (!arrayNode || arrayNode.kind !== "array") return undefined;
    return arrayNode.items.find((item) => {
        if (item.kind !== "object") return false;
        const prop = item.properties.find((p) => p.key === key);
        return prop !== undefined && prop.value.kind === "scalar" && prop.value.value === value;
    });
}
